(() => {
  const layer = () => {
    window.renvariDataLayer = Array.isArray(window.renvariDataLayer) ? window.renvariDataLayer : [];
    return window.renvariDataLayer;
  };

  const track = (event, detail = {}) => {
    const payload = { event, ...detail };
    layer().push(payload);
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('renvari:analytics', { detail: payload }));
  };

  const cleanTag = (value) => value.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
  const readStored = (key) => {
    try {
      const stored = sessionStorage.getItem(key);
      if (stored) return stored;
    } catch (error) {
      // Fall through to the short-lived cookie used across Shopify's form redirect.
    }
    const prefix = `${encodeURIComponent(key)}=`;
    const cookie = document.cookie.split('; ').find((item) => item.startsWith(prefix));
    return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : '';
  };
  const writeStored = (key, value) => {
    try { sessionStorage.setItem(key, value); } catch (error) {
      // The cookie below remains available if session storage is restricted.
    }
    document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; Path=/; Max-Age=1800; SameSite=Lax`;
  };
  const removeStored = (key) => {
    try { sessionStorage.removeItem(key); } catch (error) {
      // Clear the cookie even if session storage is restricted.
    }
    document.cookie = `${encodeURIComponent(key)}=; Path=/; Max-Age=0; SameSite=Lax`;
  };

  class RenvariHeader extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-rv-menu-toggle]');
      this.panel = this.querySelector('[data-rv-mobile-panel]');
      if (!this.toggle || !this.panel) return;
      this.toggle.addEventListener('click', () => this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true'));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.panel.addEventListener('click', (event) => {
        if (event.target.closest('a')) this.setOpen(false);
      });
    }

    setOpen(open) {
      this.toggle.setAttribute('aria-expanded', String(open));
      this.panel.hidden = !open;
      document.documentElement.style.overflow = open ? 'hidden' : '';
      if (open) this.panel.querySelector('a, button')?.focus();
      else this.toggle.focus();
    }

    onKeydown(event) {
      if (this.panel.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.setOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [this.toggle, ...this.panel.querySelectorAll('a[href], button:not([disabled])')];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('renvari-header')) customElements.define('renvari-header', RenvariHeader);

  document.addEventListener('DOMContentLoaded', () => {
    const observeOnce = (element, event, detail = {}, threshold = 0.35) => {
      if (!element) return;
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          track(event, detail);
          observer.disconnect();
        }
      }, { threshold });
      observer.observe(element);
    };

    document.querySelectorAll('.rv2-profile').forEach((profile, index) => {
      const fragrances = ['NOIR', 'VELOCE', 'RIVIERA'];
      observeOnce(profile, 'renvari_fragrance_view', { fragrance: fragrances[index] || 'unknown' }, 0.55);
    });

    observeOnce(document.querySelector('#early-access'), 'renvari_early_access_view', {}, 0.3);

    document.querySelectorAll('[data-rv-early-access-cta]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.dataset.rvPlacement === 'hero') track('renvari_hero_cta_click', { placement: 'hero' });
      });
    });

    document.querySelectorAll('[data-rv-waitlist]').forEach((container) => {
      const form = container.querySelector('form');
      const tagInput = container.querySelector('[data-rv-tags]');
      const preferences = [...container.querySelectorAll('[data-rv-preference]')];
      const email = container.querySelector('input[type="email"]');
      const consent = container.querySelector('input[name="contact[accepts_marketing]"]');
      const submit = container.querySelector('[data-rv-signup-submit]');
      let started = false;
      const context = container.dataset.rvContext || 'flagship';
      const feedback = container.querySelector('[data-rv-signup-success], .rv2-form-errors');
      const storageKey = `renvari_fragrance_${context}`;
      const params = new URLSearchParams(window.location.search);
      const campaignTags = ['utm_source', 'utm_campaign']
        .map((key) => params.get(key) ? `RENVARI_${key.toUpperCase()}_${cleanTag(params.get(key))}` : '')
        .filter(Boolean);
      const selectedPreference = () => preferences.find((input) => input.checked)?.value || '';
      const updateTags = () => {
        const baseTag = context === 'air_one' ? 'RENVARI_AIR_ONE_INTEREST' : 'RENVARI_EARLY_ACCESS';
        const fragrance = selectedPreference();
        const preferenceTag = fragrance ? `RENVARI_${cleanTag(fragrance)}` : '';
        if (tagInput) tagInput.value = [baseTag, preferenceTag, ...campaignTags].filter(Boolean).join(', ');
      };
      const setError = (name, visible) => {
        const error = container.querySelector(`[data-rv-error="${name}"]`);
        if (error) error.hidden = !visible;
        if (name === 'email' && email) email.setAttribute('aria-invalid', String(visible));
      };

      form?.addEventListener('input', () => {
        if (started) return;
        started = true;
        track('renvari_early_access_start', { context });
      });

      preferences.forEach((preference) => preference.addEventListener('change', () => {
        updateTags();
        setError('fragrance', false);
        writeStored(storageKey, preference.value);
        track('renvari_fragrance_select', { fragrance: preference.value, context });
      }));

      email?.addEventListener('input', () => setError('email', false));
      consent?.addEventListener('change', () => setError('consent', false));

      const storedPreference = readStored(storageKey);
      if (storedPreference && preferences.length) {
        const storedInput = preferences.find((input) => input.value === storedPreference);
        if (storedInput) storedInput.checked = true;
      }
      updateTags();

      form?.addEventListener('submit', (event) => {
        const fragrance = selectedPreference();
        const invalidEmail = !email?.value.trim() || !email.checkValidity();
        const missingFragrance = context !== 'air_one' && !fragrance;
        const missingConsent = !consent?.checked;
        setError('email', invalidEmail);
        setError('fragrance', missingFragrance);
        setError('consent', missingConsent);
        if (invalidEmail || missingFragrance || missingConsent) {
          event.preventDefault();
          const reasons = [invalidEmail ? 'email' : '', missingFragrance ? 'fragrance' : '', missingConsent ? 'consent' : ''].filter(Boolean);
          track('renvari_early_access_error', { context, reason: reasons.join(',') });
          if (invalidEmail) email.focus();
          else if (missingFragrance) preferences[0]?.focus();
          else consent?.focus();
          return;
        }
        updateTags();
        if (fragrance) writeStored(storageKey, fragrance);
        track('renvari_early_access_submit', { context, fragrance: fragrance || undefined });
        if (submit) {
          submit.disabled = true;
          submit.setAttribute('aria-disabled', 'true');
        }
      });

      if (container.querySelector('[data-rv-signup-success]')) {
        const fragrance = readStored(storageKey);
        const successPreference = container.querySelector('[data-rv-success-fragrance]');
        if (fragrance && successPreference) {
          successPreference.textContent = `Избран аромат: ${fragrance}`;
          successPreference.hidden = false;
        }
        track('renvari_early_access_success', { context, fragrance: fragrance || undefined });
        removeStored(storageKey);
      }

      if (container.querySelector('[data-rv-server-error]')) track('renvari_early_access_error', { context, reason: 'server' });

      feedback?.focus();
    });
  });
})();
